import {
    Combobox,
    Transition,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    Field,
} from '@headlessui/react';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { selectMainNumberOptions } from 'flow/selectors/profile-selectors';
import React, { Fragment, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { useParams } from 'react-router-dom';

export interface CallerIdOption {
    id: string;
    name: string;
}

interface Props {
    /**
     * Names the field for assistive tech. The visible label sits outside this
     * component, so only the caller can say what this combobox is for.
     */
    ariaLabel?: string;
    caller: CallerIdOption;
    onChange: (newValue: CallerIdOption) => void;
    enableMobileOption?: boolean;
    editable: boolean;
}

const CallerIdCombobox = ({
    caller,
    ariaLabel,
    onChange,
    enableMobileOption = true,
    editable,
}: Props) => {
    const { i18next } = useAppContext();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();

    const { getProfile } = useProfiles(extensionKey);

    const mainNumbers = useAppSelector(selectMainNumberOptions);

    const [query, setQuery] = useState('');
    const profile = getProfile(profileKey);

    const defaultOptions: CallerIdOption[] = profile
        .getIn(['callerId', 'callerIdOptions'])
        .filter(
            (id) =>
                (id !== 'msisdn' || enableMobileOption) &&
                (id !== 'hidden' || !IS_UAE_HOST),
        )
        .map((id) => ({
            id,
            name: {
                fixed: i18next.t('settings.landline_number'),
                msisdn: i18next.t('settings.mobile_number'),
                hidden: i18next.t('settings.hidden_number'),
            }[id],
        }))
        .concat(mainNumbers);

    const normalizedQuery = query.toLowerCase().replace(/\s+/g, '');
    const filteredCallIds =
        normalizedQuery === ''
            ? defaultOptions
            : defaultOptions.filter((option) =>
                  option.name
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(normalizedQuery),
              );

    return (
        <Field disabled={!editable}>
            <Combobox value={caller} by="id" onChange={onChange}>
                <div className="relative w-full text-sm/4">
                    <ComboboxInput
                        aria-label={ariaLabel}
                        className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-9 text-gray-900"
                        displayValue={(option: CallerIdOption) => option.name}
                        onChange={(event) => setQuery(event.target.value)}
                    />

                    <ComboboxButton className="absolute inset-y-0 end-0 flex items-center pe-2">
                        <Icon
                            className="size-5 text-gray-600"
                            iconKey="arrow_down"
                            aria-hidden="true"
                        />
                    </ComboboxButton>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery('')}
                    >
                        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                            {filteredCallIds.map((option) => (
                                <ComboboxOption
                                    key={option.id}
                                    className={({ focus }) =>
                                        twMerge(
                                            'relative cursor-default py-2 ps-3 pe-9 select-none',
                                            focus
                                                ? 'bg-gray-200'
                                                : 'text-gray-900',
                                        )
                                    }
                                    value={option}
                                >
                                    {({ selected }) => (
                                        <span
                                            className={twMerge(
                                                'truncate text-gray-500',
                                                selected && 'font-medium',
                                            )}
                                        >
                                            {option.name}
                                        </span>
                                    )}
                                </ComboboxOption>
                            ))}
                        </ComboboxOptions>
                    </Transition>
                </div>
            </Combobox>
        </Field>
    );
};

export default CallerIdCombobox;
